import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import apiClient from '@/services/api';
import { hasCapability } from '@/lib/capabilities';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { ChevronLeft, Wallet, ArrowDownCircle, ArrowUpCircle, FileText, Lock, Unlock, RefreshCw } from 'lucide-react';

const rupees = (paise) => `₹${((paise || 0) / 100).toFixed(2)}`;
const CASH_IN_REASONS = ['CHANGE_FLOAT', 'CORRECTION', 'OTHER'];
const CASH_OUT_REASONS = ['PETTY_EXPENSE', 'BANK_DEPOSIT', 'SUPPLIER_PAYMENT', 'CORRECTION', 'OTHER'];

const Row = ({ label, value, strong, testid }) => (
  <div className="flex items-center justify-between py-1.5">
    <span className={`text-sm ${strong ? 'font-bold text-slate-900' : 'text-slate-500'}`}>{label}</span>
    <span className={`${strong ? 'text-xl font-bold text-sspice' : 'text-sm font-semibold text-slate-700'}`} data-testid={testid}>{value}</span>
  </div>
);

export default function CashDrawer() {
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const storeId = user?.store_id;
  const canManage = hasCapability(user, 'cash.manage');

  const [state, setState] = useState(null);      // {open, session, tallies}
  const [openingFloat, setOpeningFloat] = useState('');
  const [mv, setMv] = useState(null);            // {type} for movement dialog
  const [mvAmount, setMvAmount] = useState('');
  const [mvReason, setMvReason] = useState('');
  const [mvNote, setMvNote] = useState('');
  const [closing, setClosing] = useState(false);
  const [counted, setCounted] = useState('');
  const [closeNotes, setCloseNotes] = useState('');
  const [zReport, setZReport] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await apiClient.get(`/cash/current/${storeId}`, { params: { _: Date.now() } });
      setState(r.data);
    } catch { toast.error('Failed to load drawer'); }
  }, [storeId]);

  useEffect(() => {
    if (!user?.id) { navigate('/login'); return; }
    if (!canManage) { toast.error('No permission'); navigate('/pos-dashboard'); return; }
    load();
    // eslint-disable-next-line
  }, [load]);

  const openDrawer = async () => {
    const f = parseFloat(openingFloat);
    if (isNaN(f) || f < 0) { toast.error('Enter a valid opening float'); return; }
    try { await apiClient.post('/cash/open', { store_id: storeId, opening_float: f }); toast.success('Drawer opened'); setOpeningFloat(''); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed to open'); }
  };

  const submitMovement = async () => {
    const a = parseFloat(mvAmount);
    if (isNaN(a) || a <= 0) { toast.error('Enter a valid amount'); return; }
    if (!mvReason) { toast.error('Choose a reason'); return; }
    try {
      await apiClient.post('/cash/movement', { session_id: state.session.id, type: mv.type, amount: a, reason_code: mvReason, note: mvNote || null });
      toast.success(`${mv.type === 'CASH_IN' ? 'Cash in' : 'Cash out'} recorded`);
      setMv(null); setMvAmount(''); setMvReason(''); setMvNote(''); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const closeDrawer = async () => {
    const c = parseFloat(counted);
    if (isNaN(c) || c < 0) { toast.error('Enter counted cash'); return; }
    try {
      const r = await apiClient.post('/cash/close', { session_id: state.session.id, counted_cash: c, notes: closeNotes || null });
      setZReport(r.data); setClosing(false); setCounted(''); setCloseNotes(''); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to close'); }
  };

  if (!state) return <div className="p-8 text-center text-slate-400">Loading…</div>;

  const t = state.tallies;
  const liveVariance = closing && counted !== '' ? Math.round(parseFloat(counted || 0) * 100) - (t?.expected_cash_paise || 0) : null;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="cash-drawer-page">
      <div className="bg-white border-b border-slate-200 px-4 py-3 flex items-center justify-between sticky top-0 z-30">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate('/pos-dashboard')} data-testid="cash-back"><ChevronLeft className="w-4 h-4" /></Button>
          <div className="flex items-center gap-2">
            <Wallet className="w-5 h-5 text-sspice" />
            <div>
              <h1 className="font-heading text-lg font-bold text-slate-900">Cash Drawer</h1>
              <p className="text-xs text-slate-500">Opening float · cash movements · day-end close</p>
            </div>
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={load} data-testid="cash-refresh"><RefreshCw className="w-4 h-4" /></Button>
      </div>

      <div className="max-w-lg mx-auto p-4 space-y-4">
        {!state.open ? (
          <div className="bg-white rounded-xl border border-slate-200 p-6 text-center space-y-4" data-testid="cash-open-form">
            <Unlock className="w-10 h-10 text-sspice mx-auto" />
            <p className="text-sm text-slate-600">No drawer is open. Enter the opening float to start the day.</p>
            <Input type="number" placeholder="Opening float (₹)" value={openingFloat} onChange={(e) => setOpeningFloat(e.target.value)} data-testid="cash-opening-float" />
            <Button className="w-full bg-sspice hover:bg-sspice-hover" onClick={openDrawer} data-testid="cash-open-btn">Open Drawer</Button>
          </div>
        ) : (
          <>
            <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="cash-summary">
              <p className="text-xs text-slate-400 mb-2">Opened by {state.session.opened_by_name} · {new Date(state.session.opened_at).toLocaleString()}</p>
              <Row label="Opening Float" value={rupees(t.opening_float_paise)} testid="cash-opening" />
              <Row label="Cash Sales" value={rupees(t.cash_sales_paise)} testid="cash-sales" />
              <Row label="Cash In" value={rupees(t.cash_in_paise)} testid="cash-in-total" />
              <Row label="Cash Out" value={`- ${rupees(t.cash_out_paise)}`} testid="cash-out-total" />
              <Row label="Cash Refunds" value={`- ${rupees(t.cash_refunds_paise)}`} testid="cash-refunds" />
              <div className="border-t border-slate-100 mt-2 pt-2">
                <Row label="Expected in Drawer" value={rupees(t.expected_cash_paise)} strong testid="cash-expected" />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-2">
              <Button variant="outline" className="text-emerald-600" onClick={() => { setMv({ type: 'CASH_IN' }); setMvReason(''); }} data-testid="cash-in-btn"><ArrowDownCircle className="w-4 h-4 mr-1" />Cash In</Button>
              <Button variant="outline" className="text-amber-600" onClick={() => { setMv({ type: 'CASH_OUT' }); setMvReason(''); }} data-testid="cash-out-btn"><ArrowUpCircle className="w-4 h-4 mr-1" />Cash Out</Button>
              <Button variant="outline" className="text-slate-600" onClick={() => setClosing(true)} data-testid="cash-close-btn"><Lock className="w-4 h-4 mr-1" />Close</Button>
            </div>
          </>
        )}
      </div>

      {/* Movement dialog */}
      <Dialog open={!!mv} onOpenChange={(o) => { if (!o) setMv(null); }}>
        <DialogContent className="max-w-sm" data-testid="cash-movement-dialog">
          <DialogHeader>
            <DialogTitle>{mv?.type === 'CASH_IN' ? 'Cash In' : 'Cash Out'}</DialogTitle>
            <DialogDescription className="sr-only">Record a cash movement with a mandatory reason.</DialogDescription>
          </DialogHeader>
          <Input type="number" placeholder="Amount (₹)" value={mvAmount} onChange={(e) => setMvAmount(e.target.value)} data-testid="cash-mv-amount" />
          <div className="grid grid-cols-2 gap-2">
            {(mv?.type === 'CASH_IN' ? CASH_IN_REASONS : CASH_OUT_REASONS).map((r) => (
              <button key={r} onClick={() => setMvReason(r)} data-testid={`cash-mv-reason-${r}`}
                className={`text-xs py-2 rounded-lg border ${mvReason === r ? 'bg-sspice text-white border-sspice' : 'border-slate-200 text-slate-600'}`}>{r.replace(/_/g, ' ')}</button>
            ))}
          </div>
          <Input placeholder="Note (optional)" value={mvNote} onChange={(e) => setMvNote(e.target.value)} data-testid="cash-mv-note" />
          <Button className="w-full bg-sspice hover:bg-sspice-hover" onClick={submitMovement} data-testid="cash-mv-submit">Record</Button>
        </DialogContent>
      </Dialog>

      {/* Close dialog */}
      <Dialog open={closing} onOpenChange={(o) => { if (!o) setClosing(false); }}>
        <DialogContent className="max-w-sm" data-testid="cash-close-dialog">
          <DialogHeader>
            <DialogTitle>Close Drawer (Z-Report)</DialogTitle>
            <DialogDescription className="sr-only">Count the drawer and close the shift.</DialogDescription>
          </DialogHeader>
          <div className="bg-slate-50 rounded-lg p-3">
            <Row label="Expected" value={rupees(t?.expected_cash_paise)} testid="cash-close-expected" />
          </div>
          <Input type="number" placeholder="Counted cash (₹)" value={counted} onChange={(e) => setCounted(e.target.value)} data-testid="cash-counted" />
          {liveVariance !== null && (
            <div className={`text-center text-sm font-semibold ${liveVariance === 0 ? 'text-green-600' : liveVariance > 0 ? 'text-blue-600' : 'text-red-600'}`} data-testid="cash-live-variance">
              Variance: {liveVariance >= 0 ? '+' : '-'}{rupees(Math.abs(liveVariance))} {liveVariance === 0 ? '(balanced)' : liveVariance > 0 ? '(over)' : '(short)'}
            </div>
          )}
          <Input placeholder="Notes (optional)" value={closeNotes} onChange={(e) => setCloseNotes(e.target.value)} data-testid="cash-close-notes" />
          <Button className="w-full bg-sspice hover:bg-sspice-hover" onClick={closeDrawer} data-testid="cash-close-confirm">Close & Generate Z-Report</Button>
        </DialogContent>
      </Dialog>

      {/* Z-Report */}
      <Dialog open={!!zReport} onOpenChange={(o) => { if (!o) setZReport(null); }}>
        <DialogContent className="max-w-sm" data-testid="cash-zreport-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><FileText className="w-4 h-4" /> Z-Report #{zReport?.session?.z_report_number}</DialogTitle>
            <DialogDescription className="sr-only">Day-end Z report with variance.</DialogDescription>
          </DialogHeader>
          {zReport && (
            <div className="space-y-1 text-sm">
              <Row label="Opening Float" value={rupees(zReport.tallies.opening_float_paise)} />
              <Row label="Cash Sales" value={rupees(zReport.tallies.cash_sales_paise)} />
              <Row label="Cash In" value={rupees(zReport.tallies.cash_in_paise)} />
              <Row label="Cash Out" value={`- ${rupees(zReport.tallies.cash_out_paise)}`} />
              <Row label="Cash Refunds" value={`- ${rupees(zReport.tallies.cash_refunds_paise)}`} />
              <div className="border-t border-slate-100 pt-1">
                <Row label="Expected" value={rupees(zReport.session.expected_cash_paise)} />
                <Row label="Counted" value={rupees(zReport.session.counted_cash_paise)} />
                <Row label="Variance" strong testid="cash-z-variance"
                  value={`${zReport.session.variance_paise >= 0 ? '+' : '-'}${rupees(Math.abs(zReport.session.variance_paise))}`} />
              </div>
              <p className="text-xs text-slate-400 pt-1">Closed by {zReport.session.closed_by_name} · {new Date(zReport.session.closed_at).toLocaleString()}</p>
            </div>
          )}
          <Button variant="outline" className="w-full" onClick={() => setZReport(null)} data-testid="cash-z-done">Done</Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
